// /src/utils/location_manager.js
import {
  getHybridLocation,
  getLastCachedLocation,
} from "./geo_location";

/**
 * Location orchestration for Smart Alert UI.
 *
 * Priority:
 * 1) Fresh browser/device geolocation
 * 2) Previously trusted browser/device last-known location
 * 3) Coarse IP fallback only when no trusted browser/device location exists
 *
 * Critical rules:
 * - "last_location" stores trusted browser/device fixes only.
 * - IP must never overwrite the user's trusted last-known location.
 * - A stale trusted browser/device fix is still a valid "Last Known Location".
 * - Timestamps represent the actual fix time and are never refreshed merely
 *   because a cached fix was read or rewritten.
 */

const CACHE_KEY = "last_location";

const SOURCE_DEVICE = "device";
const SOURCE_WIFI = "wifi";
const SOURCE_BROWSER = "browser";
const SOURCE_IP = "ip";

/** ---------- Persisted trusted "last known" fix ---------- */

const toNum = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const n = Number(value);
  return Number.isFinite(n)
    ? n
    : null;
};

const normalizeFix = (loc) => {
  if (!loc) return null;

  const lat = toNum(loc.lat);
  const lon = toNum(loc.lon);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    return null;
  }

  const timestamp = (() => {
    try {
      const d = new Date(
        loc.timestamp
      );

      if (!Number.isNaN(d.getTime())) {
        return d.toISOString();
      }
    } catch {
      // ignore
    }

    return new Date().toISOString();
  })();

  return {
    lat,
    lon,
    accuracy:
      Number.isFinite(
        Number(loc.accuracy)
      )
        ? Number(loc.accuracy)
        : null,
    source: String(
      loc.source || "unknown"
    ).toLowerCase(),
    timestamp,
    city: loc.city || null,
    region: loc.region || null,
    country: loc.country || null,
    provider: loc.provider || null,
    precision: loc.precision || null,
  };
};

const isTrustedFix = (loc) => {
  const normalized = normalizeFix(
    loc
  );

  if (!normalized) {
    return false;
  }

  return [
    SOURCE_DEVICE,
    SOURCE_WIFI,
    SOURCE_BROWSER,
  ].includes(
    normalized.source
  );
};

export const getLastLocation = () => {
  try {
    // geo_location.js owns legacy migration and trusted-cache validation.
    const canonical = getLastCachedLocation();

    if (
      canonical &&
      isTrustedFix(canonical)
    ) {
      return normalizeFix(
        canonical
      );
    }

    // Defensive direct read in case this module is loaded before migration.
    const raw = localStorage.getItem(
      CACHE_KEY
    );

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(
      raw
    );

    return isTrustedFix(parsed)
      ? normalizeFix(parsed)
      : null;
  } catch {
    return null;
  }
};

export const setLastLocation = (loc) => {
  try {
    const normalized = normalizeFix(
      loc
    );

    // Never persist IP/unknown as the user's canonical last-known location.
    if (
      !normalized ||
      !isTrustedFix(normalized)
    ) {
      return false;
    }

    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(normalized)
    );

    return true;
  } catch {
    return false;
  }
};

/** ---------- Helpers ---------- */

const nowIso = () =>
  new Date().toISOString();

const parseTs = (ts) => {
  const t = Date.parse(
    ts || 0
  );

  return Number.isFinite(t)
    ? t
    : 0;
};

const sourceRank = (source) => {
  const value = String(
    source || ""
  ).toLowerCase();

  if (value === SOURCE_DEVICE) return 4;
  if (value === SOURCE_WIFI) return 3;
  if (value === SOURCE_BROWSER) return 3;
  if (value === SOURCE_IP) return 1;

  return 0;
};

/**
 * Decide whether cand is better than base.
 *
 * Trusted device/browser sources always outrank IP.
 * Within the same source class:
 *   smaller accuracy -> newer timestamp.
 */
const isBetterFix = (
  base,
  cand
) => {
  const current = normalizeFix(
    base
  );
  const candidate = normalizeFix(
    cand
  );

  if (!candidate) {
    return false;
  }

  if (!current) {
    return true;
  }

  const rBase = sourceRank(
    current.source
  );
  const rCand = sourceRank(
    candidate.source
  );

  if (rCand !== rBase) {
    return rCand > rBase;
  }

  const accBase =
    typeof current.accuracy === "number"
      ? current.accuracy
      : Infinity;

  const accCand =
    typeof candidate.accuracy === "number"
      ? candidate.accuracy
      : Infinity;

  if (accCand !== accBase) {
    return accCand < accBase;
  }

  return (
    parseTs(candidate.timestamp) >
    parseTs(current.timestamp)
  );
};

const getCacheState = (
  maxAgeMs
) => {
  const cached = getLastLocation();

  if (!cached) {
    return {
      cached: null,
      fresh: false,
      ageMs: null,
    };
  }

  const ageMs = Math.max(
    0,
    Date.now() -
      parseTs(cached.timestamp)
  );

  return {
    cached,
    fresh:
      Number.isFinite(maxAgeMs)
        ? ageMs <= maxAgeMs
        : true,
    ageMs,
  };
};

/** Query geolocation permission state if supported. */
const getGeoPermission = async () => {
  try {
    if (
      !("permissions" in navigator) ||
      !navigator.permissions?.query
    ) {
      return "unknown";
    }

    const res =
      await navigator.permissions.query({
        name: "geolocation",
      });

    return res.state;
  } catch {
    return "unknown";
  }
};

/** Promise wrapper around getCurrentPosition with an explicit timeout. */
const getCurrentPositionOnce = (
  opts,
  timeoutMs
) =>
  new Promise(
    (resolve, reject) => {
      if (
        typeof navigator === "undefined" ||
        !("geolocation" in navigator)
      ) {
        reject(
          new Error(
            "geolocation_unavailable"
          )
        );
        return;
      }

      let done = false;

      const finishResolve = (
        value
      ) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value);
      };

      const finishReject = (
        error
      ) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(
          error ||
            new Error(
              "device_error"
            )
        );
      };

      const timer = setTimeout(
        () => {
          finishReject(
            new Error(
              "device_timeout"
            )
          );
        },
        Math.max(
          250,
          timeoutMs
        )
      );

      try {
        navigator.geolocation.getCurrentPosition(
          finishResolve,
          finishReject,
          opts
        );
      } catch (error) {
        finishReject(error);
      }
    }
  );

/** iOS heuristic for slight timeout tuning. */
const isIOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(
    navigator.userAgent
  ) &&
  !window.MSStream;

/** ---------- Main API: fast first, then improve briefly ---------- */

/**
 * requestFastThenImprove
 *
 * - Immediately exposes the trusted cached last-known fix, even when stale.
 * - Attempts a quick browser/device fix.
 * - Uses getHybridLocation only as a last-resort source; updated geo_location.js
 *   itself refuses to let IP outrank a trusted browser/device cache.
 * - IP may be exposed via onUpdate only when no trusted location exists.
 * - IP is NEVER persisted to last_location.
 * - Opens a short high-accuracy watch window to improve the trusted fix.
 * - Auto-stops on visibility hidden; returns { stop }.
 *
 * @param {Object} opts
 *  - windowMs: improvement window (default 9000)
 *  - onUpdate: (loc) => void
 *  - onEvent: (evt) => void
 *  - cacheMaxAgeMs: freshness marker for telemetry/UI only (default 10 min)
 *  - quickTimeoutMs: device quick timeout (default 2500, iOS 1800)
 */
export const requestFastThenImprove = ({
  windowMs = 9000,
  onUpdate,
  onEvent,
  cacheMaxAgeMs =
    10 * 60 * 1000,
  quickTimeoutMs =
    isIOS ? 1800 : 2500,
} = {}) => {
  let stopped = false;
  let watchId = null;
  let windowTimer = null;

  const emit = (
    type,
    detail
  ) => {
    try {
      onEvent &&
        onEvent({
          type,
          detail,
          ts: nowIso(),
        });
    } catch {
      // ignore telemetry errors
    }
  };

  const cacheState =
    getCacheState(
      cacheMaxAgeMs
    );

  // "best" keeps the last trusted browser/device fix even when stale.
  let best =
    cacheState.cached || null;

  if (best) {
    try {
      onUpdate &&
        onUpdate(best);
    } catch {
      // ignore consumer callback errors
    }

    emit(
      cacheState.fresh
        ? "cache_used"
        : "cache_stale_used",
      {
        ageMs:
          cacheState.ageMs,
        source:
          best.source,
      }
    );
  }

  emit("start");

  const accept = (
    candidate,
    label
  ) => {
    if (
      stopped ||
      !candidate
    ) {
      return;
    }

    const cand = normalizeFix(
      candidate
    );

    if (!cand) {
      return;
    }

    const trusted =
      isTrustedFix(cand);

    // If we already have a trusted fix, IP/unknown can never replace it.
    if (
      best &&
      isTrustedFix(best) &&
      !trusted
    ) {
      emit(
        "candidate_rejected",
        {
          label,
          source:
            cand.source,
          reason:
            "trusted_fix_already_available",
        }
      );
      return;
    }

    if (
      !best ||
      isBetterFix(
        best,
        cand
      )
    ) {
      best = cand;

      // Only trusted browser/device locations become canonical last_location.
      if (trusted) {
        setLastLocation(
          cand
        );
      }

      try {
        onUpdate &&
          onUpdate(cand);
      } catch {
        // ignore consumer callback errors
      }

      emit(
        "update",
        {
          label,
          source:
            cand.source,
          accuracy:
            cand.accuracy,
          persisted:
            trusted,
        }
      );
    }
  };

  let stop = () => {};

  const onVisibility = () => {
    if (document.hidden) {
      stop(
        "visibility_hidden"
      );
    }
  };

  stop = (
    reason = "manual_stop"
  ) => {
    if (stopped) {
      return;
    }

    stopped = true;

    if (windowTimer) {
      clearTimeout(
        windowTimer
      );
      windowTimer = null;
    }

    if (
      watchId !== null &&
      typeof navigator !==
        "undefined" &&
      "geolocation" in navigator
    ) {
      try {
        navigator.geolocation.clearWatch(
          watchId
        );
      } catch {
        // ignore
      }

      watchId = null;
    }

    document.removeEventListener(
      "visibilitychange",
      onVisibility
    );

    emit(
      "stop",
      { reason }
    );
  };

  document.addEventListener(
    "visibilitychange",
    onVisibility,
    { passive: true }
  );

  (async () => {
    const perm =
      await getGeoPermission();

    emit(
      "permission",
      { state: perm }
    );

    // Phase 1: quick browser/device fix.
    if (
      perm !== "denied"
    ) {
      try {
        const pos =
          await getCurrentPositionOnce(
            {
              enableHighAccuracy:
                false,
              // Browser may use a recent GPS/Wi-Fi/cellular cached fix.
              maximumAge: 30000,
              timeout:
                quickTimeoutMs,
            },
            quickTimeoutMs
          );

        accept(
          {
            lat:
              pos.coords.latitude,
            lon:
              pos.coords.longitude,
            accuracy:
              pos.coords
                .accuracy ?? null,
            source:
              SOURCE_DEVICE,
            timestamp:
              Number.isFinite(
                pos.timestamp
              )
                ? new Date(
                    pos.timestamp
                  ).toISOString()
                : nowIso(),
          },
          "device_quick"
        );
      } catch (error) {
        emit(
          "device_quick_failed",
          {
            error: String(
              error?.message ||
                error
            ),
          }
        );
      }
    } else {
      emit(
        "device_skipped_denied"
      );
    }

    // Phase 1b: coherent hybrid fallback.
    //
    // geo_location.js now guarantees:
    // - trusted cache beats IP
    // - IP is returned only when no trusted browser/device history exists
    // - IP is never written into last_location
    try {
      const hybrid =
        await getHybridLocation();

      accept(
        hybrid,
        "hybrid"
      );
    } catch (error) {
      emit(
        "hybrid_failed",
        {
          error: String(
            error?.message ||
              error
          ),
        }
      );
    }

    // Phase 2: high-accuracy browser/device improvement window.
    if (
      !stopped &&
      typeof navigator !==
        "undefined" &&
      "geolocation" in navigator &&
      (
        perm === "granted" ||
        perm === "prompt" ||
        perm === "unknown"
      )
    ) {
      try {
        watchId =
          navigator.geolocation.watchPosition(
            (pos) => {
              accept(
                {
                  lat:
                    pos.coords
                      .latitude,
                  lon:
                    pos.coords
                      .longitude,
                  accuracy:
                    pos.coords
                      .accuracy ??
                    null,
                  source:
                    SOURCE_DEVICE,
                  timestamp:
                    Number.isFinite(
                      pos.timestamp
                    )
                      ? new Date(
                          pos.timestamp
                        ).toISOString()
                      : nowIso(),
                },
                "watch_improve"
              );
            },
            (error) => {
              emit(
                "watch_error",
                {
                  error: String(
                    error?.message ||
                      error
                  ),
                }
              );
            },
            {
              enableHighAccuracy:
                true,
              maximumAge: 0,
              timeout: 12000,
            }
          );

        emit(
          "watch_started"
        );
      } catch (error) {
        emit(
          "watch_start_failed",
          {
            error: String(
              error?.message ||
                error
            ),
          }
        );
      }
    }

    if (!stopped) {
      windowTimer =
        setTimeout(
          () =>
            stop(
              "window_elapsed"
            ),
          Math.max(
            0,
            windowMs
          )
        );
    }
  })();

  return { stop };
};
